import { holdersApi } from '@/api/holders.api';
import { useToolSystem } from '@/context/ToolSystemContext';
import type { CreateHolder, HolderModel, UpdateHolder } from '@/types/tool.types';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

/** Holders of the active tool system; the two catalogs name their holders independently. */
export const useHolders = () => {
  const { toolSystem } = useToolSystem();
  const [holders, setHolders] = useState<HolderModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHolders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await holdersApi.getAll(toolSystem);
      setHolders(data);
    } catch (_err) {
      const message = 'Failed to load holders';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [toolSystem]);

  useEffect(() => {
    fetchHolders();
  }, [fetchHolders]);

  const addHolder = useCallback(
    async (body: CreateHolder): Promise<void> => {
      try {
        await holdersApi.create(body);
        await fetchHolders();
        toast.success('Holder added');
      } catch (err) {
        toast.error('Failed to add holder');
        throw err;
      }
    },
    [fetchHolders]
  );

  const updateHolder = useCallback(async (holderId: string, body: UpdateHolder): Promise<HolderModel | null> => {
    try {
      const updated = await holdersApi.update(holderId, body);
      setHolders((prev) => prev.map((h) => (h.id === holderId ? updated : h)));
      return updated;
    } catch (err) {
      throw err;
    }
  }, []);

  const deleteHolder = useCallback(async (holderId: string): Promise<void> => {
    try {
      await holdersApi.delete(holderId);
      setHolders((prev) => prev.filter((h) => h.id !== holderId));
      toast.success('Holder deleted');
    } catch (err) {
      toast.error('Failed to delete holder');
      throw err;
    }
  }, []);

  return {
    holders,
    isLoading,
    error,
    refetch: fetchHolders,
    addHolder,
    updateHolder,
    deleteHolder,
  };
};
